import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FaLink, FaShieldAlt, FaPlus, FaTimes, FaRoute, FaSave } from "react-icons/fa";

import { useRoutePoints, toLocationString } from "./hooks/useRoutePoints";
import { useRouteCalculation } from "./hooks/useRouteCalculation";
import RouteMap from "./components/RouteMap";
import GenerateFenceModal from "./components/GenerateFenceModal";
import { ToastComponent } from "../Components/ToastComponent";
import { controlService } from "../../Services/control.service";
import { routingService } from "../../Services/routing.service";
import { IoMdArrowBack } from "react-icons/io";
import {
  appendTravelModeToGoogleMapsUrl,
  parseLatLngString,
  parseOriginDestinationFromMapLink,
} from "./utils/googleMaps";
import { parseIntermediatePoints } from "./utils/routePointsParser";

const TYPES = { ADDRESS: "address", COORDINATE: "coordinate", VISIT_POINT: "visitPoint" };
const DEFAULT_VEHICLE_TYPE = "Drive";


const normalizeVehicleTypeOption = (item) => {
  if (typeof item === "string") {
    const value = item.trim();
    return value ? { value, label: value, routerTypeId: null } : null;
  }
  if (!item || typeof item !== "object") return null;
  const value =
    item.apiType ?? item.ApiType ??
    item.value ?? item.Value ?? item.vehicleType ?? item.VehicleType ??
    item.code ?? item.Code ?? item.name ?? item.Name ??
    item.description ?? item.Description;
  if (!value) return null;
  const label =
    item.routerTypeName ?? item.RouterTypeName ??
    item.label ?? item.Label ?? item.description ?? item.Description ??
    item.title ?? item.Title ?? value;
  const routerTypeId = item.routerTypeId ?? item.RouterTypeId ?? null;
  return { value: String(value), label: String(label), routerTypeId };
};

const normalizeVehicleTypeOptions = (response) => {
  const items = Array.isArray(response)
    ? response
    : Array.isArray(response?.items) ? response.items
    : Array.isArray(response?.data) ? response.data
    : response ? [response] : [];
  const options = items.map(normalizeVehicleTypeOption).filter(Boolean);
  return options.length
    ? options
    : [{ value: DEFAULT_VEHICLE_TYPE, label: DEFAULT_VEHICLE_TYPE, routerTypeId: null }];
};

const normalizeVehicleTypeSelection = (value) => {
  if (typeof value !== "string") return DEFAULT_VEHICLE_TYPE;
  const trimmed = value.trim();
  return trimmed || DEFAULT_VEHICLE_TYPE;
};


const buildInstructionsText = (calcResult) => {
  const item = calcResult?.items?.[0] ?? calcResult?.Items?.[0] ?? null;
  const steps = item?.steps ?? item?.Steps ?? [];
  if (!Array.isArray(steps) || steps.length === 0) return "";

  return steps
    .slice()
    .sort((a, b) => (a.order ?? a.Order ?? 0) - (b.order ?? b.Order ?? 0))
    .map((step, idx) => {
      const instruction = step.instruction ?? step.Instruction ?? "";
      const distanceText = step.distance?.text ?? step.Distance?.Text ?? "";
      const durationText = step.duration?.text ?? step.Duration?.Text ?? "";
      const suffix = [distanceText, durationText].filter(Boolean).join(" · ");
      return `${idx + 1}. ${instruction}${suffix ? ` (${suffix})` : ""}`;
    })
    .join("\n");
};

// ─── Google Autocomplete Input ────────────────────────────────────────────────
// Recebe `initialValue` para pré-preencher o campo (não-controlado) quando o
// formulário é remontado a partir de uma rota salva (#363). Sem isso, o input
// só ganha valor quando o usuário escolhe algo no autocomplete.
function AddressInput({ id, initialValue, onPlaceSelected }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    let retryTimeout;
    const init = () => {
      if (!window.google?.maps?.places || !inputRef.current) {
        retryTimeout = setTimeout(init, 200);
        return;
      }
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ["geometry", "formatted_address"],
        componentRestrictions: { country: ["br"] },
      });
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place?.geometry?.location) {
          const address = place.formatted_address || place.name || "";
          if (inputRef.current) inputRef.current.value = address;
          onPlaceSelected?.({ address, latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng() });
        }
      });
    };
    init();
    return () => {
      clearTimeout(retryTimeout);
      if (autocompleteRef.current)
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      className="form-control"
      placeholder="Digite o endereço completo…"
      defaultValue={initialValue || ""}
      autoComplete="off"
      style={{ fontSize: 13, width: "100%" }}
    />
  );
}

// ─── Type Selector Pills ──────────────────────────────────────────────────────
function TypePills({ value, onChange, t, disabled }) {
  const opts = [
    { val: TYPES.ADDRESS, label: t("ROUTING.TYPE_ADDRESS") },
    { val: TYPES.COORDINATE, label: t("ROUTING.TYPE_COORDINATE") },
    { val: TYPES.VISIT_POINT, label: t("ROUTING.TYPE_VISIT_POINT") },
  ];
  return (
    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
      {opts.map((o) => (
        <button
          key={o.val}
          type="button"
          onClick={() => !disabled && onChange(o.val)}
          disabled={disabled}
          style={{
            padding: "2px 10px", fontSize: 11, borderRadius: 20,
            border: `1px solid ${value === o.val ? "var(--theme-primary,#f97316)" : "var(--border-color,#444)"}`,
            background: value === o.val ? "var(--theme-primary,#f97316)" : "transparent",
            color: value === o.val ? "#fff" : "var(--text-secondary,#aaa)",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
            transition: "all .15s", whiteSpace: "nowrap",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── Single Point Row (compact, horizontal) ──────────────────────────────────
// #363: `type`/`lat`/`lng` agora são inicializados a partir de `point` (que já
// funcionava só pro `type`). Isso só tem efeito de fato quando o componente é
// REMONTADO (ver `formVersion` mais abaixo, usado como parte da `key`), já que
// useState só lê o valor inicial uma vez por instância do componente.
function PointInput({ pointId, label, required, point, onChange, visitOptions, loadingVisits, t, onRemove, readOnly }) {
  const [type, setType] = useState(point?.type || TYPES.ADDRESS);
  const [lat, setLat] = useState(point?.latitude != null ? String(point.latitude) : "");
  const [lng, setLng] = useState(point?.longitude != null ? String(point.longitude) : "");
  const addrKeyRef = useRef(0);

  const handleTypeChange = (newType) => {
    setType(newType);
    setLat(""); setLng("");
    addrKeyRef.current += 1;
    onChange({ type: newType, label: "", location: "", latitude: null, longitude: null });
  };

  const handleAddressSelected = ({ address, latitude, longitude }) => {
    onChange({ type: TYPES.ADDRESS, label: address, location: address, latitude, longitude });
  };

  const handleLatLng = (field, val) => {
    const newLat = field === "lat" ? val : lat;
    const newLng = field === "lng" ? val : lng;
    if (field === "lat") setLat(val);
    else setLng(val);
    const latN = Number(newLat), lngN = Number(newLng);
    if (newLat !== "" && newLng !== "" && !isNaN(latN) && !isNaN(lngN)) {
      const location = `${newLat},${newLng}`;
      onChange({ type: TYPES.COORDINATE, label: location, location, latitude: latN, longitude: lngN });
    } else {
      onChange({ type: TYPES.COORDINATE, label: "", location: "", latitude: null, longitude: null });
    }
  };

  const handleVisit = (e) => {
    const id = Number(e.target.value);
    const opt = visitOptions.find((o) => o.markerId === id);
    if (opt) {
      const location = `${opt.latitude},${opt.longitude}`;
      onChange({ type: TYPES.VISIT_POINT, label: opt.markerDescription, location, latitude: Number(opt.latitude), longitude: Number(opt.longitude), visitPointId: opt.markerId });
    } else {
      onChange({ type: TYPES.VISIT_POINT, label: "", location: "", latitude: null, longitude: null });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 0",
        borderBottom: "1px solid var(--border-color,#333)",
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: 11, fontWeight: 700, color: "var(--text-secondary,#aaa)",
          textTransform: "uppercase", letterSpacing: "0.06em",
          minWidth: 78, flexShrink: 0,
        }}
      >
        {label}
        {required && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
      </span>

      <TypePills value={type} onChange={handleTypeChange} t={t} disabled={readOnly} />

      <div style={{ flex: 1, minWidth: 240 }}>
        {type === TYPES.ADDRESS && (
          <AddressInput
            key={`addr-${pointId}-${addrKeyRef.current}`}
            id={`addr-${pointId}-${addrKeyRef.current}`}
            initialValue={point?.label}
            onPlaceSelected={handleAddressSelected}
          />
        )}

        {type === TYPES.COORDINATE && (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Latitude  ex: -23.5468"
              value={lat}
              onChange={(e) => handleLatLng("lat", e.target.value)}
              style={{ fontSize: 13 }}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Longitude  ex: -46.6353"
              value={lng}
              onChange={(e) => handleLatLng("lng", e.target.value)}
              style={{ fontSize: 13 }}
            />
          </div>
        )}

        {type === TYPES.VISIT_POINT && (
          <select
            className="form-control"
            value={point?.visitPointId ?? ""}
            onChange={handleVisit}
            disabled={loadingVisits}
            style={{ fontSize: 13 }}
          >
            <option value="">{loadingVisits ? "Carregando pontos…" : "Selecione um ponto de visita"}</option>
            {visitOptions.map((o) => (
              <option key={o.markerId} value={o.markerId}>{o.markerDescription}</option>
            ))}
          </select>
        )}
      </div>

      {onRemove && !readOnly && (
        <button
          type="button"
          onClick={onRemove}
          title="Remover"
          style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: "2px 4px", fontSize: 11, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          <FaTimes size={11} /> Remover
        </button>
      )}
    </div>
  );
}

// ─── Add Intermediate Button ──────────────────────────────────────────────────
function AddIntermediateButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "none",
        border: "1px dashed var(--border-color,#444)",
        borderRadius: 8, padding: "5px 14px",
        cursor: "pointer", color: "var(--text-secondary,#aaa)",
        fontSize: 12, transition: "all .15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--theme-primary,#f97316)"; e.currentTarget.style.color = "var(--theme-primary,#f97316)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-color,#444)"; e.currentTarget.style.color = "var(--text-secondary,#aaa)"; }}
    >
      <FaPlus size={10} /> Adicionar destino intermediário
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Routing() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch({ type: "title", value: t("ROUTING.TITLE") });
  }, [dispatch, t]);

  const {
    origin, setOrigin,
    destination, setDestination,
    points, addPoint, updatePoint, removePoint,
    loadRoute,
    canCalculateRoute, buildRoutingPayload, isAtMaxIntermediates,
  } = useRoutePoints();

  const { routeResult, calculating, error, calculate, setRouteResult } = useRouteCalculation();

  const [showMapModal, setShowMapModal] = useState(false);
  const autoOpenMapRef = useRef(!!location.state?.autoOpenMap);
  const [showFenceModal, setShowFenceModal] = useState(false);
  const [toastParams, setToastParams] = useState("");
  const [visitOptions, setVisitOptions] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([
    { value: DEFAULT_VEHICLE_TYPE, label: DEFAULT_VEHICLE_TYPE, routerTypeId: null },
  ]);
  const [vehicleType, setVehicleType] = useState(() =>
    normalizeVehicleTypeSelection(
      location.state?.vehicleType ??
        location.state?.VehicleType ??
        location.state?.route?.vehicleType ??
        location.state?.route?.VehicleType
    )
  );
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(false);

  const [isRouting, setIsRouting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [optimizePath, setOptimizePath] = useState(() => {
    const routeData = location.state?.route;
    const val = routeData?.optimized ?? routeData?.Optimized;
    return val != null ? !!val : true;
  });

  // #363: incrementado sempre que uma rota salva é carregada no formulário.
  // Usado como parte da `key` de Origem/Destino pra FORÇAR o remount desses
  // campos — sem isso, o <input> de endereço (não-controlado) e o `type`
  // interno do PointInput não refletem os dados carregados via `loadRoute`,
  // porque só lêem o valor inicial na primeira montagem do componente.
  const [formVersion, setFormVersion] = useState(0);


  const [isEditMode, setIsEditMode] = useState(!!location.state?.route);

  // Trava síncrona contra duplo-clique/duplo-toque: `isRouting`/`isSaving`
  // (state) só refletem no `disabled` do botão depois do re-render, então um
  // segundo clique bem rápido pode passar antes do botão desabilitar de fato
  // e disparar a ação duas vezes (2x POST /routes). Um ref muda na hora, sem
  // esperar ciclo do React, e fecha essa brecha.
  const isRoutingRef = useRef(false);
  const isSavingRef = useRef(false);
  const isSilentCalculatingRef = useRef(false);
  // Guarda o path (polyline) e o mapLink já salvos da rota, pra reaproveitar
  // no "Ver Mapa" em vez de recalcular — recalcular regeocodifica o endereço
  // em texto do zero e pode divergir do caminho/endereço originalmente salvo.
  const savedRouteMetaRef = useRef({ path: null, mapLink: null });

  useEffect(() => {
    setIsSaved(false);
  }, [origin, destination, points, vehicleType, optimizePath]);

  // Modo "Ver detalhes": não existe botão ROTEIRIZAR pra popular `routeResult`,
  // então Ver Mapa / Copiar link / Gerar cerca ficariam sempre desabilitados.
  // Assim que `loadRoute` termina de setar origin/destination/points
  // (sinalizado por `formVersion` mudando), recalculamos em silêncio pra
  // preencher `routeResult` — sem chamar `saveRoute`, já que a rota já existe.
  useEffect(() => {
    if (!isEditMode || formVersion === 0) return;
    if (!canCalculateRoute) return;
    if (isSilentCalculatingRef.current) return;

    const savedPath = savedRouteMetaRef.current.path;
    if (savedPath) {
      // Reaproveita o path já salvo em vez de recalcular — recalcular
      // regeocodifica o endereço em texto do zero e pode divergir do
      // caminho/endereço que foi salvo originalmente.
      setRouteResult({
        items: [
          {
            encodedPolyline: savedPath,
            origin: {
              latitude: origin.latitude ?? null,
              longitude: origin.longitude ?? null,
              address: { formattedAddress: origin.label || toLocationString(origin) },
            },
            destination: {
              latitude: destination.latitude ?? null,
              longitude: destination.longitude ?? null,
              address: { formattedAddress: destination.label || toLocationString(destination) },
            },
            googleMapsUrl: savedRouteMetaRef.current.mapLink ?? null,
          },
        ],
      });
      setIsSaved(true);
      return;
    }

    isSilentCalculatingRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const payload = buildRoutingPayload(vehicleType);
        payload.optimized = optimizePath && points.length > 0;
        const result = await calculate(payload);
        if (!cancelled && result) {
          setIsSaved(true);
        }
      } finally {
        isSilentCalculatingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, formVersion, canCalculateRoute, vehicleType]);


  useEffect(() => {
    if (!autoOpenMapRef.current || !isSaved) return;
    setShowMapModal(true);
    autoOpenMapRef.current = false;
  }, [isSaved]);

  useEffect(() => {
    setLoadingVisits(true);
    controlService.findCoordinate(2)
      .then((res) => { if (Array.isArray(res)) setVisitOptions(res); })
      .finally(() => setLoadingVisits(false));
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoadingVehicleTypes(true);
    routingService.fetchVehicleTypes()
      .then((res) => {
        if (!isMounted) return;
        const options = normalizeVehicleTypeOptions(res);
        setVehicleTypeOptions(options);
        setVehicleType((currentVehicleType) => {
          const selectedExists = options.some((option) => option.value === currentVehicleType);
          return selectedExists ? currentVehicleType : options[0]?.value ?? DEFAULT_VEHICLE_TYPE;
        });
      })
      .finally(() => { if (isMounted) setLoadingVehicleTypes(false); });
    return () => { isMounted = false; };
  }, []);

  // #363 + bug novo: ao vir de "Ver detalhes", os intermediários NÃO vêm no
  // GET /routes (listagem) — eles vivem em GET /routes/{routeId}/points.
  // Por isso buscamos explicitamente aqui, em vez de confiar em
  // routeData.intermediatePoints (campo que não existe nesse endpoint).
  useEffect(() => {
    const routeData = location.state?.route;
    if (!routeData) return;

    let cancelled = false;

    const load = async () => {
  console.log("ROUTE DATA:", JSON.stringify(routeData, null, 2));
  const mapLink = routeData.mapLink ?? routeData.MapLink;
      savedRouteMetaRef.current = {
        path: routeData.path ?? routeData.Path ?? null,
        mapLink: mapLink ?? null,
      };
      let originName =
        routeData.startLocation ?? routeData.StartLocation ??
        routeData.originName ?? routeData.OriginName;
      let destinationName =
        routeData.endLocation ?? routeData.EndLocation ??
        routeData.destinationName ?? routeData.DestinationName;

      if (!originName || !destinationName) {
        const parsed = parseOriginDestinationFromMapLink(mapLink);
        if (!originName) originName = parsed.origin;
        if (!destinationName) destinationName = parsed.destination;
      }

      const toFormPoint = (value) => {
        if (!value) return { type: TYPES.ADDRESS, label: "", location: "", latitude: null, longitude: null };
        const coord = parseLatLngString(value);
        if (coord) {
          return { type: TYPES.COORDINATE, label: value, location: value, latitude: coord.lat, longitude: coord.lng };
        }
        return { type: TYPES.ADDRESS, label: value, location: value, latitude: null, longitude: null };
      };


      const rawIntermediatesField = routeData.intermediatePoints ?? routeData.IntermediatePoints ?? "";
      let intermediatePoints = parseIntermediatePoints(rawIntermediatesField).map((p) => ({
        type: p.type || TYPES.ADDRESS,
        label: p.label || "",
        location:
          p.label ||
          (p.latitude != null && p.longitude != null ? `${p.latitude},${p.longitude}` : ""),
        latitude: p.latitude,
        longitude: p.longitude,
        visitPointId: p.visitPointId,
      }));


      const routeId = routeData.routeId ?? routeData.RouteId;
      if (routeId) {
        try {
          const rawPoints = await routingService.fetchRoutePoints(routeId);
          const pointsArray = Array.isArray(rawPoints)
            ? rawPoints
            : Array.isArray(rawPoints?.items) ? rawPoints.items
            : Array.isArray(rawPoints?.data) ? rawPoints.data
            : [];

          if (pointsArray.length > 0) {
            intermediatePoints = pointsArray
              .map((p) => ({
                order: p.order ?? p.Order ?? 0,
                label: p.address ?? p.Address ?? p.label ?? p.Label ?? "",
                latitude: p.latitude ?? p.Latitude ?? null,
                longitude: p.longitude ?? p.Longitude ?? null,
                visitPointId: p.visitPointId ?? p.VisitPointId ?? null,
              }))
              .sort((a, b) => a.order - b.order)
              .map((p) => {
                const coord = p.label ? parseLatLngString(p.label) : null;
                const type = p.visitPointId
                  ? TYPES.VISIT_POINT
                  : coord
                  ? TYPES.COORDINATE
                  : TYPES.ADDRESS;
                const location =
                  p.label ||
                  (p.latitude != null && p.longitude != null ? `${p.latitude},${p.longitude}` : "");
                return {
                  type,
                  label: p.label || location,
                  location,
                  latitude: p.latitude,
                  longitude: p.longitude,
                  visitPointId: p.visitPointId,
                };
              });
          }
        } catch (err) {
          console.error("Erro ao buscar pontos intermediários da rota:", err);
        }
      }

      if (cancelled) return;

      loadRoute({
        origin: toFormPoint(originName),
        destination: toFormPoint(destinationName),
        points: intermediatePoints,
      });
      setFormVersion((v) => v + 1);
      setIsEditMode(true);
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (title, body, color = "success") =>
    setToastParams(JSON.stringify({ show: true, title, body, color }));

  const handleAddPoint = () => {
    addPoint({ type: TYPES.ADDRESS, label: "", location: "", latitude: null, longitude: null });
  };


  const saveRoute = async (calcResult) => {
    setIsSaving(true);
    try {
      const resolvedVehicleType =
        calcResult?.items?.[0]?.apiType ?? calcResult?.items?.[0]?.ApiType ??
        calcResult?.apiType ?? calcResult?.ApiType;
      if (resolvedVehicleType) {
        setVehicleType(normalizeVehicleTypeSelection(resolvedVehicleType));
      }

      // RouteTypeId (int) é o que o POST /routes espera — diferente do apiType
      // (string) usado no /calculate. Buscamos na lista de opções carregada.
      const effectiveVehicleType = resolvedVehicleType
        ? normalizeVehicleTypeSelection(resolvedVehicleType)
        : vehicleType;
      const selectedOption = vehicleTypeOptions.find(
        (option) => option.value === effectiveVehicleType
      );
      const routeTypeId = selectedOption?.routerTypeId;

      if (!routeTypeId) {
        showToast(
          t("COMMON.ERROR"),
          "Não foi possível identificar o tipo de roteirização selecionado.",
          "danger"
        );
        return null;
      }

      const item = calcResult?.items?.[0] ?? calcResult?.Items?.[0] ?? null;
      const encodedPolyline = item?.encodedPolyline ?? item?.EncodedPolyline ?? "";
      const backendMapLink = item?.googleMapsUrl ?? item?.GoogleMapsUrl;

      if (!backendMapLink) {
        showToast(
          t("COMMON.ERROR"),
          "O backend não retornou o link da rota (googleMapsUrl). A rota não pode ser salva assim — avise o time de backend.",
          "danger"
        );
        return null;
      }

      const createPayload = {
        routeTypeId,
        startLocation: origin.label || toLocationString(origin),
        endLocation: destination.label || toLocationString(destination),
        optimized: optimizePath,
        path: encodedPolyline,
        areaPath: "",
        mapLink: appendTravelModeToGoogleMapsUrl(backendMapLink, vehicleType),
        instructions: buildInstructionsText(calcResult),
      };

      const saved = await routingService.createRoute(createPayload);
      const routeId = saved?.routeId ?? saved?.RouteId ?? saved;

      if (!saved || !routeId) {
        showToast(t("COMMON.ERROR"), "Erro ao salvar a roteirização", "danger");
        return null;
      }

      const orderedPoints = points.slice().sort((a, b) => a.order - b.order);
      for (const point of orderedPoints) {
        await routingService.createRoutePoint({
          routeId,
          order: point.order,
          address: point.label || "",
          latitude: point.latitude,
          longitude: point.longitude,
          visitPointId: point.visitPointId ?? null,
        });
      }

      setIsSaved(true);
      showToast(t("COMMON.SUCCESS"), "Roteirização salva com sucesso");
      return saved;
    } finally {
      setIsSaving(false);
    }
  };

  const handleRoteirizar = async () => {
    if (!canCalculateRoute || isRoutingRef.current) return;
    isRoutingRef.current = true;
    setIsRouting(true);
    try {
      const payload = buildRoutingPayload(vehicleType);
      payload.optimized = optimizePath && points.length > 0;
      const result = await calculate(payload);
      if (!result) {
        showToast(t("COMMON.ERROR"), t("ROUTING.ERROR_CALCULATE_ROUTE"), "danger");
        return;
      }

      if (autoSave) {
        await saveRoute(result);
      } else {
        showToast(t("COMMON.SUCCESS"), "Roteirização calculada. Clique em Salvar para confirmar.");
      }
    } finally {
      setIsRouting(false);
      isRoutingRef.current = false;
    }
  };

  const handleSaveClick = async () => {
    if (!routeResult || isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      await saveRoute(routeResult);
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleCopyLink = () => {
    if (!isSaved) return;

    const backendMapLink =
      routeResult?.items?.[0]?.googleMapsUrl ?? routeResult?.Items?.[0]?.GoogleMapsUrl;

    if (!backendMapLink) {
      showToast(t("COMMON.ERROR"), t("ROUTING.LINK_COPY_ERROR"), "danger");
      return;
    }

    const link = appendTravelModeToGoogleMapsUrl(backendMapLink, vehicleType);
    navigator.clipboard.writeText(link)
      .then(() => showToast(t("COMMON.SUCCESS"), t("ROUTING.LINK_COPIED")))
      .catch(() => showToast(t("COMMON.ERROR"), t("ROUTING.LINK_COPY_ERROR"), "danger"));
  };

  const inputProps = { visitOptions, loadingVisits, t };

  // Botão SALVAR só aparece quando: autoSave está desligado, já tem um
  // resultado calculado e ele ainda não foi salvo.
  const showManualSaveButton = !autoSave && !!routeResult && !isSaved;

  return (
    <>
      <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />

      <div className="container-xxl flex-grow-1 container-p-y">
        <div style={{ width: "70%", minWidth: 640 }}>
          <div className="back-to-fleet" style={{ marginBottom: 8 }}>
            <button
                  style={{ display: "flex", alignItems: "center" }}
                  onClick={() => navigate(-1)}
                    >
          <div className="icon-back-fleet">
      <IoMdArrowBack />
    </div>
    <span>{t("FLEET.BACK")}</span>
  </button>
</div>

          <div className="card" style={{ padding: "20px 24px" }}>

            {/* ── Veículo ── */}
            <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--border-color,#333)" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary,#aaa)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Veículo <span style={{ color: "#ef4444" }}>*</span>
              </span>
              <select
                className="form-control mt-1"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                disabled={loadingVehicleTypes || vehicleTypeOptions.length === 0}
                style={{ fontSize: 13 }}
              >
                {loadingVehicleTypes && (
                  <option value={vehicleType}>Carregando tipos de veículo…</option>
                )}
                {!loadingVehicleTypes && vehicleTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <div className="form-check mt-2" style={{ fontSize: 13 }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="optimizePathCheck"
                  checked={optimizePath}
                  onChange={(e) => setOptimizePath(e.target.checked)}
                  disabled={isEditMode}
                />
                <label className="form-check-label" htmlFor="optimizePathCheck">
                  Otimizar Caminho{isEditMode ? " (já roteirizada)" : ""}
                </label>
              </div>
            </div>

            <PointInput key={`origin-${formVersion}`} pointId="origin" label="Origem" required point={origin} onChange={setOrigin} readOnly={isEditMode} {...inputProps} />

            <PointInput key={`destination-${formVersion}`} pointId="destination" label="Destino" required point={destination} onChange={setDestination} readOnly={isEditMode} {...inputProps} />

            {points.length > 0 && points.map((p, i) => (
              <PointInput
                key={p.id}
                pointId={p.id}
                label={`Parada ${String(i + 1).padStart(2, "0")}`}
                point={p}
                onChange={(data) => updatePoint(p.id, data)}
                onRemove={() => removePoint(p.id)}
                readOnly={isEditMode}
                {...inputProps}
              />
            ))}

            {!isAtMaxIntermediates && !isEditMode && (
              <div style={{ paddingTop: 10 }}>
                <AddIntermediateButton onClick={handleAddPoint} />
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2 mt-3" style={{ fontSize: 13 }}>{error}</div>
            )}

            {/* ── Opção de salvar automático ── */}
            {!isEditMode && (
              <div className="form-check" style={{ fontSize: 13, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-color,#333)" }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="autoSaveCheck"
                  checked={autoSave}
                  onChange={(e) => setAutoSave(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="autoSaveCheck">
                  Salvar automaticamente ao roteirizar
                </label>
              </div>
            )}

            {/* ── Actions ── */}
              <div style={{ marginTop: 12, paddingTop: 16, borderTop: "1px solid var(--border-color,#333)", display: "flex", gap: 10, flexWrap: "wrap" }}>
                {!isEditMode && (
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleRoteirizar}
                disabled={!canCalculateRoute || isRouting || calculating}
                style={{ fontSize: 13 }}
              >
                {isRouting || calculating
                ? <><span className="spinner-border spinner-border-sm" /> Roteirizando…</>
                : <><FaRoute size={13} /> ROTEIRIZAR</>}
              </button>
)}

              {showManualSaveButton && (
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={handleSaveClick}
                  disabled={isSaving}
                  style={{ fontSize: 13 }}
                >
                  {isSaving
                    ? <><span className="spinner-border spinner-border-sm" /> Salvando…</>
                    : <><FaSave size={13} /> SALVAR</>}
                </button>
              )}

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => setShowMapModal(true)}
                disabled={!isSaved}
                style={{ fontSize: 13 }}
              >
                {t("ROUTING.VIEW_MAP")}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={handleCopyLink}
                disabled={!isSaved}
                style={{ fontSize: 13 }}
              >
                <FaLink size={13} /> {t("ROUTING.COPY_LINK")}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => setShowFenceModal(true)}
                disabled={!isSaved}
                style={{ fontSize: 13 }}
              >
                <FaShieldAlt size={13} /> {t("ROUTING.GENERATE_FENCE")}
              </button>
            </div>

          </div>
        </div>
      </div>

      <RouteMap
        show={showMapModal}
        onHide={() => setShowMapModal(false)}
        routeResult={routeResult}
        origin={origin}
        destination={destination}
        points={points}
        vehicleType={vehicleType}
      />

      <GenerateFenceModal
        show={showFenceModal}
        onHide={() => setShowFenceModal(false)}
        routeResult={routeResult}
        origin={origin}
        destination={destination}
        onSuccess={() => showToast(t("COMMON.SUCCESS"), t("ELETRONIC_FENCES.SAVED_SUCCESS"))}
      />
    </>
  );
}
